/**
 * ProjectsManager Component
 * Admin view for managing all projects across clients
 */

import React, { useState, useEffect } from 'react';
import { projectApi } from '../services/api';
import AdminProjectDetail from './AdminProjectDetail';

interface Project {
  id: number;
  project_number: string;
  name: string;
  client_name: string;
  client_code?: string | null;
  assigned_to: string;
  status: string;
  progress: number;
  priority: string;
  due_date: string;
  created_at: string;
  document_count?: number | null;
  total_pages?: number | null;
  dominant_csi?: string | null;
}

interface ProjectsManagerProps {
  onNewProject?: () => void;
}

const ProjectsManager: React.FC<ProjectsManagerProps> = ({ onNewProject }) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({
    status: 'all',
    assignee: 'all',
    search: ''
  });
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  useEffect(() => {
    fetchProjects();
  }, [filter]);

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const params: { status?: string } = {};
      if (filter.status !== 'all') {
        params.status = filter.status;
      }
      const response = await projectApi.list(params);
      if (response.success && response.data) {
        // Map API response to component's Project interface (defensive: new
        // list fields may be null or absent depending on backend version)
        const mappedProjects = (response.data.projects || []).map((p: any) => ({
          ...p,
          assigned_to: p.assigned_to || 'Unassigned',
          client_name: p.client_name || p.client_code || 'Unknown Client',
          client_code: p.client_code ?? null,
          due_date: p.due_date || '',
          document_count: typeof p.document_count === 'number' ? p.document_count : 0,
          total_pages: typeof p.total_pages === 'number' ? p.total_pages : 0,
          dominant_csi: typeof p.dominant_csi === 'string' && p.dominant_csi ? p.dominant_csi : null,
        }));
        setProjects(mappedProjects);
      } else {
        console.error('Failed to fetch projects:', response.error);
        setProjects([]);
      }
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredProjects = projects.filter(project => {
    if (filter.status !== 'all' && project.status !== filter.status) return false;
    if (filter.assignee !== 'all' && project.assigned_to !== filter.assignee) return false;
    if (filter.search) {
      const search = filter.search.toLowerCase();
      if (!project.name.toLowerCase().includes(search) &&
          !project.project_number.toLowerCase().includes(search) &&
          !project.client_name.toLowerCase().includes(search)) {
        return false;
      }
    }
    return true;
  });

  const assignees = [...new Set(projects.map(p => p.assigned_to))];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'intake': return 'var(--secondary)';
      case 'queued': return 'var(--info, #06b6d4)';
      case 'in_progress': return 'var(--primary)';
      case 'review': return 'var(--warning)';
      case 'completed': return 'var(--success)';
      default: return 'var(--secondary)';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'var(--danger)';
      case 'high': return 'var(--warning)';
      case 'normal': return 'var(--secondary)';
      case 'low': return 'var(--success)';
      default: return 'var(--secondary)';
    }
  };

  // Stats
  const stats = {
    total: projects.length,
    intake: projects.filter(p => p.status === 'intake').length,
    inProgress: projects.filter(p => p.status === 'in_progress').length,
    review: projects.filter(p => p.status === 'review').length,
    urgent: projects.filter(p => p.priority === 'urgent').length,
  };

  return (
    <div className="projects-manager">
      <div className="manager-header">
        <h2>Project Management</h2>
        <button className="btn primary" onClick={onNewProject}>+ New Project</button>
      </div>

      {/* Stats */}
      <div className="stats-row">
        <div className="stat-pill">
          <span className="stat-value">{stats.total}</span>
          <span className="stat-label">Total</span>
        </div>
        <div className="stat-pill intake">
          <span className="stat-value">{stats.intake}</span>
          <span className="stat-label">Intake</span>
        </div>
        <div className="stat-pill progress">
          <span className="stat-value">{stats.inProgress}</span>
          <span className="stat-label">In Progress</span>
        </div>
        <div className="stat-pill review">
          <span className="stat-value">{stats.review}</span>
          <span className="stat-label">Review</span>
        </div>
        {stats.urgent > 0 && (
          <div className="stat-pill urgent">
            <span className="stat-value">{stats.urgent}</span>
            <span className="stat-label">Urgent</span>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search projects..."
            value={filter.search}
            onChange={(e) => setFilter({ ...filter, search: e.target.value })}
          />
        </div>

        <select
          value={filter.status}
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}
        >
          <option value="all">All Status</option>
          <option value="intake">Intake</option>
          <option value="queued">Queued</option>
          <option value="in_progress">In Progress</option>
          <option value="review">Review</option>
          <option value="completed">Completed</option>
        </select>

        <select
          value={filter.assignee}
          onChange={(e) => setFilter({ ...filter, assignee: e.target.value })}
        >
          <option value="all">All Assignees</option>
          {assignees.map(a => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
      </div>

      {/* Projects Table */}
      {loading ? (
        <div className="loading">Loading projects...</div>
      ) : filteredProjects.length === 0 ? (
        <div className="empty-state">
          <svg
            className="empty-state-icon"
            width="48"
            height="48"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
            <path d="M12 11v4" />
            <path d="M10 13h4" />
          </svg>
          <p className="empty-state-title">No active shop drawing packages found.</p>
          <p className="empty-state-subline">Get started by creating your first project.</p>
          {onNewProject && (
            <button className="btn primary" onClick={() => onNewProject()}>
              + New Project
            </button>
          )}
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Project</th>
              <th>Client</th>
              <th>Division</th>
              <th>Docs</th>
              <th>Pages</th>
              <th>Assigned To</th>
              <th>Status</th>
              <th>Progress</th>
              <th>Priority</th>
              <th>Due Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredProjects.map(project => (
              <tr key={project.id} onClick={() => setSelectedProject(project)}>
                <td>
                  <div className="project-cell">
                    <span className="project-number">{project.project_number}</span>
                    <span className="project-name">{project.name}</span>
                  </div>
                </td>
                <td>
                  <div className="client-cell">
                    <span>{project.client_name}</span>
                    {project.client_code ? (
                      <span className="client-code">{project.client_code}</span>
                    ) : null}
                  </div>
                </td>
                <td>
                  {project.dominant_csi ? (
                    <span className="csi-chip">{project.dominant_csi}</span>
                  ) : (
                    <span className="muted-dash">&mdash;</span>
                  )}
                </td>
                <td>{project.document_count ?? 0}</td>
                <td>{project.total_pages ?? 0}</td>
                <td>
                  <span className={project.assigned_to === 'Unassigned' ? 'unassigned' : ''}>
                    {project.assigned_to}
                  </span>
                </td>
                <td>
                  <span
                    className="status-badge"
                    style={{ backgroundColor: `${getStatusColor(project.status)}20`, color: getStatusColor(project.status) }}
                  >
                    {project.status.replace('_', ' ')}
                  </span>
                </td>
                <td>
                  <div className="progress-cell">
                    <div className="mini-progress">
                      <div
                        className="progress-fill"
                        style={{ width: `${project.progress}%`, backgroundColor: getStatusColor(project.status) }}
                      ></div>
                    </div>
                    <span>{project.progress}%</span>
                  </div>
                </td>
                <td>
                  <span
                    className="priority-badge"
                    style={{ backgroundColor: `${getPriorityColor(project.priority)}20`, color: getPriorityColor(project.priority) }}
                  >
                    {project.priority}
                  </span>
                </td>
                <td>{project.due_date ? new Date(project.due_date).toLocaleDateString() : '—'}</td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedProject(project);
                      }}
                    >
                      View
                    </button>
                    <button className="btn small" disabled title="Coming soon">Assign</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Project detail console */}
      {selectedProject && (
        <AdminProjectDetail
          projectId={selectedProject.id}
          onClose={() => setSelectedProject(null)}
        />
      )}

      <style>{`
        .projects-manager {
          padding: 0;
        }

        .manager-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .manager-header h2 {
          margin: 0;
        }

        .stats-row {
          display: flex;
          gap: 12px;
          margin-bottom: 24px;
          flex-wrap: wrap;
        }

        .stat-pill {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 2px;
          padding: 10px 20px;
          min-width: 84px;
          background: var(--bg-tertiary, #334155);
          border-radius: 12px;
        }

        .stat-pill.intake { background: rgba(100, 116, 139, 0.2); }
        .stat-pill.progress { background: rgba(37, 99, 235, 0.2); }
        .stat-pill.review { background: rgba(234, 179, 8, 0.2); }
        .stat-pill.urgent { background: rgba(239, 68, 68, 0.2); }

        .stat-value {
          font-size: 1.5rem;
          font-weight: 700;
          line-height: 1.1;
        }

        .stat-label {
          font-size: 0.7rem;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--text-secondary);
        }

        .filters {
          display: flex;
          gap: 12px;
          margin-bottom: 16px;
          flex-wrap: wrap;
        }

        .search-box {
          flex: 1;
          min-width: 200px;
        }

        .search-box input {
          width: 100%;
          padding: 8px 16px;
          background: var(--bg-tertiary, #334155);
          border: 1px solid var(--border-color, #334155);
          border-radius: 8px;
          color: var(--text-primary);
        }

        .filters select {
          padding: 8px 16px;
          background: var(--bg-tertiary, #334155);
          border: 1px solid var(--border-color, #334155);
          border-radius: 8px;
          color: var(--text-primary);
        }

        .project-cell {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .project-number {
          font-size: 12px;
          color: var(--text-secondary);
        }

        .project-name {
          font-weight: 500;
        }

        .unassigned {
          color: var(--warning);
          font-style: italic;
        }

        .client-cell {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .client-code {
          font-size: 11px;
          color: var(--text-secondary);
          letter-spacing: 0.04em;
        }

        .csi-chip {
          display: inline-block;
          padding: 2px 8px;
          border-radius: 10px;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.04em;
          background: var(--bg-tertiary, #334155);
          color: var(--text-secondary);
          border: 1px solid var(--border-color, #475569);
          white-space: nowrap;
        }

        .muted-dash {
          color: var(--text-secondary);
          opacity: 0.6;
        }

        .empty-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 56px 24px;
          text-align: center;
          border: 1px dashed var(--border-color, #334155);
          border-radius: 12px;
          color: var(--text-secondary);
        }

        .empty-state-icon {
          color: var(--text-secondary);
          opacity: 0.7;
          margin-bottom: 4px;
        }

        .empty-state-title {
          margin: 0;
          font-size: 1rem;
          font-weight: 600;
          color: var(--text-primary);
        }

        .empty-state-subline {
          margin: 0 0 12px;
          font-size: 0.85rem;
          color: var(--text-secondary);
        }

        .status-badge, .priority-badge {
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
          text-transform: capitalize;
        }

        .progress-cell {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .mini-progress {
          width: 60px;
          height: 6px;
          background: var(--bg-tertiary, #334155);
          border-radius: 3px;
          overflow: hidden;
        }

        .progress-fill {
          height: 100%;
          border-radius: 3px;
          transition: width 0.3s;
        }

        .action-buttons {
          display: flex;
          gap: 4px;
        }

        .loading {
          text-align: center;
          padding: 40px;
          color: var(--text-secondary);
        }

        tbody tr {
          cursor: pointer;
        }

        tbody tr:hover {
          background: var(--bg-tertiary, #334155);
        }
      `}</style>
    </div>
  );
};

export default ProjectsManager;
